import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { OLModal } from "components/Tooltips/OLModal";
import OverflowChipTooltip from "components/Tooltips/OverflowChipTooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useEffect, useRef, useState } from "react";
import { KeyValuePair } from "utilities/objects";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    customAttrContainer: {
      display: "flex",
      gap: theme.spacing(4),
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    showMoreBtn: {
      fontSize: "13px",
      textDecoration: "underline",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    },
  }),
);

interface Props {
  attributes: KeyValuePair[];
}

export const CustomAttributes = ({ attributes }: Props) => {
  const [attrContainerHeight, setAttrContainerHeight] = useState(0);
  const refChipsContainer = useRef<HTMLDivElement>(null);
  const [isCustomAttributesModalOpen, setIsCustomAttributesModalOpen] =
    useState(false);
  const { message } = useMessage();
  const classes = useStyles();

  useEffect(() => {
    if (!refChipsContainer.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setAttrContainerHeight(refChipsContainer.current?.clientHeight as number);
    });
    resizeObserver.observe(refChipsContainer.current);
    return () => resizeObserver.disconnect();
  }, []);

  const maxChipsContainerHeight = 112;

  const isShowAllAttrBtn = attrContainerHeight - 1 >= maxChipsContainerHeight;

  return (
    <div className={classes.container}>
      <div
        ref={refChipsContainer}
        className={classes.customAttrContainer}
        style={{
          maxHeight: maxChipsContainerHeight + 1,
          maxWidth: "100%",
          overflow:
            attrContainerHeight > maxChipsContainerHeight
              ? "hidden"
              : "visible",
        }}
      >
        {attributes?.length > 0 ? (
          <>
            {attributes?.map((attribute: KeyValuePair, index: number) => (
              <OverflowChipTooltip
                key={index}
                placement="top"
                label={`${attribute.key}: ${attribute.value}`}
              />
            ))}
          </>
        ) : (
          "-"
        )}
      </div>
      {isShowAllAttrBtn && (
        <OLButton
          className={classes.showMoreBtn}
          onClick={() => setIsCustomAttributesModalOpen(true)}
          data-test-id="show-all"
        >
          {message(
            { id: "showAllElements" },
            {
              elements: attributes.length,
            },
          )}
        </OLButton>
      )}
      <OLModal
        open={isCustomAttributesModalOpen}
        header={message({ id: "customAttributes" })}
        submitText={message({ id: "close" })}
        submitAction={() => setIsCustomAttributesModalOpen(false)}
        onClose={() => setIsCustomAttributesModalOpen(false)}
      >
        <div className={classes.customAttrContainer}>
          {attributes.map((attribute, index) => (
            <OverflowChipTooltip
              key={index}
              placement="top"
              label={`${attribute.key}: ${attribute.value}`}
            />
          ))}
        </div>
      </OLModal>
    </div>
  );
};
